/**
 * useAvailablePlans — the purchasable plans + the caller's current plan.
 *
 * Server state via TanStack Query, keyed `["available-plans"]`. Freshness
 * mirrors useCreditBalance: the `subscription-changed` WebSocket event
 * (consumed in useUnifiedEventConsumer) invalidates this query so it refetches,
 * and a 60s staleTime keeps it cheap between reads.
 *
 * Alongside the query this module exposes the subscription-management
 * mutations:
 *   - useCreateCheckoutSession → redirects the browser to Stripe Checkout.
 *   - useOpenBillingPortal     → redirects the browser to the Stripe portal.
 *   - useChangeSubscription    → no-payment plan change (Downgrade-to-Free),
 *     invalidating every query whose result depends on the plan/subscription.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAvailablePlans,
  createCheckoutSession,
  openBillingPortal,
  changeSubscription,
  type AvailablePlansResponse,
  type PlanDto,
  type CurrentPlanDto,
  type ChangeSubscriptionEffect,
} from "@/services/billingApi";
import { CREDIT_BALANCE_QUERY_KEY } from "@/hooks/useCreditBalance";
import { BILLABLE_ACCOUNTS_QUERY_KEY } from "@/hooks/useBillableAccounts";
import { getResponseStatus } from "@/utils/errorUtils";

export const AVAILABLE_PLANS_QUERY_KEY = ["available-plans"] as const;
export const MY_SUBSCRIPTION_QUERY_KEY = ["my-subscription"] as const;

const STALE_MS = 60_000;

interface UseAvailablePlansResult {
  plans: PlanDto[];
  current: CurrentPlanDto | null;
  isLoading: boolean;
  error: Error | null;
}

export function useAvailablePlans(): UseAvailablePlansResult {
  const query = useQuery<AvailablePlansResponse>({
    queryKey: AVAILABLE_PLANS_QUERY_KEY,
    queryFn: getAvailablePlans,
    staleTime: STALE_MS,
    retry: (failureCount, error: unknown) => {
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
  });

  return {
    plans: query.data?.plans ?? [],
    current: query.data?.current ?? null,
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}

/**
 * Mutation that starts Stripe Checkout for a plan and, on success, redirects
 * the browser to the returned URL.
 */
export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: (planId: number) => createCheckoutSession(planId),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
}

/**
 * Mutation that opens the Stripe Billing Portal and, on success, redirects the
 * browser to the returned URL.
 */
export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: () => openBillingPortal(),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
}

/**
 * Mutation for a no-payment subscription change (Downgrade-to-Free). On success
 * it invalidates every query whose result depends on the plan/subscription so
 * the whole billing surface reflects the change.
 */
export function useChangeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: {
      planId: number;
      effective?: ChangeSubscriptionEffect;
    }) => changeSubscription(vars.planId, vars.effective),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_SUBSCRIPTION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: AVAILABLE_PLANS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CREDIT_BALANCE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BILLABLE_ACCOUNTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["billing-contexts"] });
    },
  });
}
